import React from 'react';
import { Book, Clock, Globe, School, BookOpen, Target, Plus } from 'lucide-react';
import { Input, Select, Textarea } from './FormComponents';
import type { Category, SubCategory, Language } from '../../../../../services/AdminService/AdminCourseManagementService';
import type { Instructor } from '../../../../../services/PagesServices/SuperAdminILTService';
import './CourseCreationTabs.css';

export interface BasicInfoTabProps {
  // Form data
  courseType: 'web' | 'ilt' | 'blended';
  courseTitle: string;
  instructorName: string;

  // ILT instructor selection
  instructors: Instructor[];
  selectedInstructorId: string;
  onInstructorChange: (value: string) => void;
  shortDescription: string;
  description: string;
  category: string;
  subcategory: string;
  language: string;
  level: 'beginner' | 'intermediate' | 'advanced';
  courseDuration: string;

  // Metadata
  categories: Category[];
  subcategories: SubCategory[];
  languages: Language[];
  loadingMetadata: boolean;

  // Handlers
  onCourseTypeChange: (type: 'web' | 'ilt' | 'blended') => void;
  onCourseTitleChange: (value: string) => void;
  onShortDescriptionChange: (value: string) => void;
  onDescriptionChange: (value: string) => void;
  onCategoryChange: (value: string) => void;
  onSubcategoryChange: (value: string) => void;
  onLanguageChange: (value: string) => void;
  onLevelChange: (value: 'beginner' | 'intermediate' | 'advanced') => void;
  onCourseDurationChange: (value: string) => void;

  // Add category/subcategory handlers
  onAddCategoryClick?: () => void;
  onAddSubcategoryClick?: () => void;

  // Batch selection

  // Validation
  validationErrors: Record<string, string>;
}

export const BasicInfoTab: React.FC<BasicInfoTabProps> = ({
  courseType,
  courseTitle,
  instructorName,
  instructors,
  selectedInstructorId,
  onInstructorChange,
  shortDescription,
  description,
  category,
  subcategory,
  language,
  level,
  courseDuration,
  categories,
  subcategories,
  languages,
  loadingMetadata,
  onCourseTypeChange,
  onCourseTitleChange,
  onShortDescriptionChange,
  onDescriptionChange,
  onCategoryChange,
  onSubcategoryChange,
  onLanguageChange,
  onLevelChange,
  onCourseDurationChange,
  onAddCategoryClick,
  onAddSubcategoryClick,
  validationErrors,
}) => {
  // Filter subcategories based on selected category
  const filteredSubcategories = subcategories.filter(
    (sub) => sub.categoryId === parseInt(category)
  );

  return (
    <div className="tab-content">
      {/* Section Header */}
      <div className="section-header basic-header">
        <h3>
          <Book size={20} />
          Course Fundamentals
        </h3>
        <p>Start by providing the basic information about your course</p>
      </div>

      {/* Course Type Selection */}
      <div className="course-type-section">
        <label className="section-label">
          <Target size={18} />
          Select Course Type *
        </label>
        <div className="course-type-grid">
          <button
            type="button"
            className={`course-type-card ${courseType === 'web' ? 'active' : ''}`}
            onClick={() => onCourseTypeChange('web')}
          >
            <div className="course-type-icon">
              <Globe size={32} />
            </div>
            <h4>Web-based Course</h4>
            <p>Self-paced online learning with modules and assessments</p>
          </button>

          <button
            type="button"
            className={`course-type-card ${courseType === 'ilt' ? 'active' : ''}`}
            onClick={() => onCourseTypeChange('ilt')}
          >
            <div className="course-type-icon">
              <School size={32} />
            </div>
            <h4>ILT Course</h4>
            <p>Instructor-led training with scheduled sessions and offerings</p>
          </button>

          <button
            type="button"
            className={`course-type-card ${courseType === 'blended' ? 'active' : ''}`}
            onClick={() => onCourseTypeChange('blended')}
          >
            <div className="course-type-icon">
              <BookOpen size={32} />
            </div>
            <h4>Blended Course</h4>
            <p>Combination of online modules and instructor-led sessions</p>
          </button>
        </div>
      </div>

      {/* Form Grid */}
      <div className="form-grid">
        {/* Left Column */}
        <div className="form-column">
          <Input
            label="Course Title"
            required
            value={courseTitle}
            onChange={(e) => onCourseTitleChange(e.target.value)}
            error={validationErrors.courseTitle}
            placeholder="e.g., Advanced Leadership Development"
          />

          <Textarea
            label="Short Description"
            value={shortDescription}
            onChange={(e) => onShortDescriptionChange(e.target.value)}
            rows={2}
            placeholder="Brief description for course card..."
          />

          <Textarea
            label="Detailed Description"
            value={description}
            onChange={(e) => onDescriptionChange(e.target.value)}
            rows={6}
            placeholder="Provide a comprehensive description of your course content, objectives, and target audience"
          />
        </div>

        {/* Right Column */}
        <div className="form-column">
          <div className="form-row">
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px' }}>
                <div style={{ flex: 1 }}>
                  <Select
                    label="Category"
                    required
                    value={category}
                    onChange={(e) => {
                      onCategoryChange(e.target.value);
                      onSubcategoryChange(''); // Reset subcategory when category changes
                    }}
                    error={validationErrors.category}
                    disabled={loadingMetadata}
                  >
                    <option value="">Select Category</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name}
                      </option>
                    ))}
                  </Select>
                </div>
                {onAddCategoryClick && (
                  <button
                    type="button"
                    onClick={onAddCategoryClick}
                    style={{
                      padding: '10px 12px',
                      backgroundColor: '#FF8C00',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: validationErrors.category ? '20px' : '0',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#e67e00'}
                    onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#FF8C00'}
                    title="Add new category"
                  >
                    <Plus size={18} />
                  </button>
                )}
              </div>
            </div>

            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px' }}>
                <div style={{ flex: 1 }}>
                  <Select
                    label="Subcategory"
                    required
                    value={subcategory}
                    onChange={(e) => onSubcategoryChange(e.target.value)}
                    error={validationErrors.subcategory}
                    disabled={!category || loadingMetadata}
                  >
                    <option value="">
                      {!category ? 'Select a category first' : 'Select Subcategory'}
                    </option>
                    {filteredSubcategories.map((sub) => (
                      <option key={sub.id} value={sub.id}>
                        {sub.name}
                      </option>
                    ))}
                  </Select>
                </div>
                {onAddSubcategoryClick && (
                  <button
                    type="button"
                    onClick={onAddSubcategoryClick}
                    disabled={!category}
                    style={{
                      padding: '10px 12px',
                      backgroundColor: category ? '#FF8C00' : '#ccc',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: category ? 'pointer' : 'not-allowed',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: validationErrors.subcategory ? '20px' : '0',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseOver={(e) => {
                      if (category) e.currentTarget.style.backgroundColor = '#e67e00';
                    }}
                    onMouseOut={(e) => {
                      if (category) e.currentTarget.style.backgroundColor = '#FF8C00';
                    }}
                    title={category ? "Add new subcategory" : "Select a category first"}
                  >
                    <Plus size={18} />
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="form-row">
            <Select
              label="Language"
              required
              value={language}
              onChange={(e) => onLanguageChange(e.target.value)}
              error={validationErrors.language}
              disabled={loadingMetadata}
            >
              <option value="">Select Language</option>
              {languages.map((lang) => (
                <option key={lang.id} value={lang.id}>
                  {lang.name}
                </option>
              ))}
            </Select>

            {courseType !== 'ilt' && (
              <Select
                label="Difficulty Level"
                value={level}
                onChange={(e) => onLevelChange(e.target.value as 'beginner' | 'intermediate' | 'advanced')}
              >
                <option value="beginner">Beginner</option>
                <option value="intermediate">Intermediate</option>
                <option value="advanced">Advanced</option>
              </Select>
            )}
          </div>

          <Input
            label="Course Duration"
            icon={Clock}
            value={courseDuration}
            onChange={(e) => onCourseDurationChange(e.target.value)}
            placeholder="e.g., 5 hours, 3 weeks"
          />

          {courseType === 'ilt' ? (
            <>
              <Select
                label="Instructor"
                value={selectedInstructorId}
                onChange={(e) => onInstructorChange(e.target.value)}
              >
                <option value="">Select an instructor</option>
                {instructors.map((ins) => (
                  <option key={ins.uuid} value={ins.uuid}>
                    {ins.firstName} {ins.lastName}
                  </option>
                ))}
              </Select>
              <small className="instructor-note">
                Assign an active instructor to lead this instructor-led course
              </small>
            </>
          ) : (
            <>
              <Input
                label="Instructor"
                value={instructorName}
                disabled
                title="Automatically set to current logged-in instructor"
              />
              <small className="instructor-note">
                Automatically assigned to you as the course creator
              </small>
            </>
          )}
        </div>
      </div>

    </div>
  );
};
